import React from 'react';
import { User, Department, Role } from '../../types';
import { Button } from '../ui/Button';
import { getDepartmentLabel } from '../../utils/departments';
import { validatePassword } from '../../utils/auth';
import { AlertTriangle } from 'lucide-react';

interface UserFormProps {
  user?: User;
  onSubmit: (userData: Partial<User> & { password?: string }) => void;
  onCancel: () => void;
}

export const UserForm: React.FC<UserFormProps> = ({ user, onSubmit, onCancel }) => {
  const [formData, setFormData] = React.useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    role: user?.role || 'EMPLOYEE' as Role,
    department: user?.department || 'SALES' as Department,
    password: '',
  });

  const [passwordErrors, setPasswordErrors] = React.useState<string[]>([]);

  const departments: Department[] = [
    'EXECUTIVE',
    'SALES',
    'PURCHASE',
    'ACCOUNTS_RECEIVABLE',
    'ACCOUNTS_PAYABLE',
    'OPERATIONS',
    'WAREHOUSE'
  ];
  
  const roles: Role[] = ['EMPLOYEE', 'MANAGER', 'CEO', 'CFO'];

  const handlePasswordChange = (password: string) => {
    setFormData(prev => ({ ...prev, password }));
    if (!user) { // Only validate password for new users
      setPasswordErrors(validatePassword(password));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate password for new users
    if (!user && passwordErrors.length > 0) {
      return;
    }

    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Name
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-emerald-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Email
        </label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
          className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-emerald-500"
          required
        />
      </div>

      {!user && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <input
            type="password"
            value={formData.password}
            onChange={(e) => handlePasswordChange(e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-emerald-500"
            required
          />
          {passwordErrors.length > 0 && (
            <div className="mt-2 space-y-1">
              {passwordErrors.map((error, index) => (
                <div key={index} className="flex items-center gap-1 text-xs text-amber-600">
                  <AlertTriangle className="w-3 h-3" />
                  <span>{error}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Phone Number
        </label>
        <input
          type="tel"
          value={formData.phone}
          onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
          placeholder="+1 (555) 555-5555"
          className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-emerald-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Role
        </label>
        <select
          value={formData.role}
          onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as Role }))}
          className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-emerald-500"
          required
        >
          {roles.map(role => (
            <option key={role} value={role}>{role}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Department
        </label>
        <select
          value={formData.department}
          onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value as Department }))}
          className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-emerald-500"
          required
        >
          {departments.map(dept => (
            <option key={dept} value={dept}>{getDepartmentLabel(dept)}</option>
          ))}
        </select>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button 
          type="submit" 
          variant="primary"
          disabled={!user && passwordErrors.length > 0}
        >
          {user ? 'Update User' : 'Add User'}
        </Button>
      </div>
    </form>
  );
};